import { createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * NET Scotland brand theme — single source of truth.
 *
 * Tokens are taken from `spec/system/theme.md` (the canonical theme) and
 * the Phase 1 UI design contract `01-UI-SPEC.md` (which collapses theme.md
 * into a four-token typography scale + a locked spacing scale per
 * gsd-ui-checker rules). Where theme.md and UI-SPEC differ on weight (e.g.
 * theme.md labels weight 500 vs UI-SPEC labels weight 600), UI-SPEC wins
 * because it is the agreed contract for executor implementation; the
 * `fontWeightMedium: 500` MUI token still resolves correctly for any
 * react-admin/MUI internals that reference it.
 *
 * Consumers (Plan 13):
 *   - `src/index.tsx` wraps the whole app in `<ThemeProvider theme={netScotlandTheme}>`.
 *   - `src/public/PublicLayout.tsx` mounts the same theme above `<Admin>`
 *     (D-12) so public routes share the brand without pulling react-admin.
 *   - `src/public/PublicCalendar.tsx` consumes `theme.palette` instead of
 *     literal hex values for the Subscribe CTA (replaces the Plan 08
 *     `BRAND_ORANGE` magic-number).
 */

// Tokens (UI-SPEC §Color + §Typography + §Spacing scale, with theme.md §2 surface palette)
export const tokens = {
    color: {
        // Surface palette
        surfaceCanvas: '#F7F9FC',
        surfaceCard: '#FFFFFF',
        surfaceRail: '#FFFFFF',
        surfaceAppBar: '#004071', // brand-navy (top app bar carries product identity)
        surfaceDivider: '#E3E8EE',
        surfaceSubtle: '#EFF3F8',
        // Brand palette
        brandNavy: '#004071',
        brandBlue: '#007CC0',
        brandOrange: '#EF8E00',
        brandSky: '#46AFD3',
        // Status palette
        statusDanger: '#C0392B',
        statusSuccess: '#2E7D4B',
        statusWarning: '#B76A00',
        statusInfo: '#007CC0',
        statusSensitive: '#6B3FA0',
        // Text palette
        textPrimary: '#1A1D21',
        textOnBrand: '#FFFFFF',
        textSecondary: '#4A5260',
        textMuted: '#8A94A3',
        textLink: '#007CC0',
        // Divider (semi-transparent navy per UI-SPEC card frames)
        divider: 'rgba(0, 64, 113, 0.12)',
    },
    radius: {
        card: 8,
        pill: 16,
    },
};

const themeOptions: ThemeOptions = {
    palette: {
        mode: 'light',
        primary: {
            main: tokens.color.brandNavy,
            contrastText: tokens.color.textOnBrand,
        },
        secondary: { main: tokens.color.brandBlue },
        error: { main: tokens.color.statusDanger },
        warning: { main: tokens.color.statusWarning },
        info: { main: tokens.color.statusInfo },
        success: { main: tokens.color.statusSuccess },
        background: {
            default: tokens.color.surfaceCanvas,
            paper: tokens.color.surfaceCard,
        },
        text: {
            primary: tokens.color.textPrimary,
            secondary: tokens.color.textSecondary,
            disabled: tokens.color.textMuted,
        },
        divider: tokens.color.divider,
    },
    typography: {
        fontFamily: 'Roboto, system-ui, -apple-system, "Segoe UI", sans-serif',
        h1: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25 }, // Display / page title
        h2: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 }, // Section title
        h3: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }, // Card title
        body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
        body2: {
            fontSize: '0.8125rem',
            fontWeight: 400,
            lineHeight: 1.4,
        },
        button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: { borderRadius: tokens.radius.card },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: tokens.color.surfaceAppBar,
                    color: tokens.color.textOnBrand,
                    boxShadow: 'none',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: tokens.color.surfaceCard,
                    borderRadius: tokens.radius.card,
                    // elevation-1 light shadow per theme.md §4
                    boxShadow:
                        '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.08)',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    // Focus ring per UI-SPEC: 2px brand-blue with 2px offset
                    '&:focus-visible': {
                        outline: `2px solid ${tokens.color.brandBlue}`,
                        outlineOffset: 2,
                    },
                },
                containedPrimary: {
                    backgroundColor: tokens.color.brandNavy,
                    color: tokens.color.textOnBrand,
                    '&:hover': { backgroundColor: '#003258' },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: tokens.color.brandBlue,
                    textDecoration: 'none',
                    '&:hover, &:focus-visible': {
                        textDecoration: 'underline',
                    },
                },
            },
        },
    },
};

export const netScotlandTheme = createTheme(themeOptions);

export default netScotlandTheme;
